// Packages
import React, { useState, useEffect, useMemo } from "react";
import useTableInstance from "hooks/useTableInstance";
import { createColumnHelper } from "@tanstack/react-table";
import { useTheme } from "@mui/material/styles";
import { cloneDeep, isEmpty } from "lodash";
import { v4 as uuidv4 } from 'uuid';

// Components
import { Box, Button, Typography, CircularProgress } from "@mui/material";
import ModalContainer from "components/ModalContainer";
import TableField from "components/Table";
import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";
import GlobalAlert from "components/Notification";

// Hook
import useAuthScope from "hooks/useAuthScope";
import useDataSourceManager from "hooks/useDataSourceManager";

// Img
import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";

// Helper
import {
  getActionSelectorSendRequestParams,
  initUpdateActionFw,
  getIsInvalid,
  getCleanSavingPayload
} from "../../helper";

const BlockAllowModal = ({
  setAlertInfo,
  actions,
  setActions,
  openedAction,
  selectedInstancesList,
  initCallback
}) => {
  const theme = useTheme();
  const columnHelper = createColumnHelper();
  const { dataMap, getData, sendRequest } = useDataSourceManager();
  const { auth } = useAuthScope();

  const [uuids] = useState(() => ({
    actionSelector: {
      blockClient: uuidv4(),
    },
  }));
  const uuid = useMemo(() => uuids.actionSelector[openedAction?.key], [openedAction?.key]);

  const [selectedList, setSelectedList] = useState(cloneDeep(selectedInstancesList));
  const demoFW = selectedList?.[0];

  const [isLoading, setIsLoading] = useState(false);
  const isInvalid = useMemo(() => getIsInvalid({
    selectedList,
    openedAction
  }), [selectedList, openedAction?.key]);

  const handleCloseModal = () => {
    setActions({
      ...cloneDeep(actions),
      [openedAction?.key]: {
        ...cloneDeep(openedAction),
        isOpenModal: false
      }
    });
  }

  const handleSubmit = () => {
    const payload = getCleanSavingPayload({
      selectedList,
      openedAction
    });

    const { apiKey, restfulParams } = getActionSelectorSendRequestParams({ auth });

    sendRequest(
      apiKey,
      uuid,
      restfulParams,
      payload
    );
  }

  const handleResetModal = () => {
    setIsLoading(false);
  }

  useEffect(() => {
    const response = getData(uuid);

    const isLoading = !response;
    const isRespError = !!response?.error || response?.code >= 400;
    const isRespSuccess = !!response?.data || !!response?.result;

    if (isLoading) {
      setIsLoading(true);
      return;
    }

    if (isRespError) {
      setIsLoading(false);

      setAlertInfo({
        open: true,
        severity: 'error',
        message: response?.message || "",
      });

      return;
    }

    if (isRespSuccess) {
      setIsLoading(false);
      initCallback();
    }

  }, [dataMap])

  return (
    <ModalContainer display={!!openedAction} modalWidth={"500px"}>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>

        <Typography
          sx={{
            color: theme.palette.primary.main,
            fontSize: theme.typography.customFontSize[5],
            fontWeight: theme.typography.customFontWeight[3],
          }}>
          {openedAction?.label?.modal || "-"}
        </Typography>

        <Button
          onClick={handleCloseModal}
          sx={{
            backgroundColor: theme.palette.primary.transparent,
            boxShadow: "none",
            "&:hover": {
              backgroundColor: theme.palette.primary.transparent,
            },
          }}>
          <CloseIcon />
        </Button>

      </Box>

      <Box sx={{ marginBottom: "20px" }}>
        <Box>
          <Typography sx={{
            color: theme.palette.primary.black,
            fontSize: theme.typography.customFontSize[2],
          }}>
            {openedAction?.label?.description(selectedList?.length ?? '0')}
          </Typography>
        </Box>
      </Box>

      {
        <>
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", marginTop: "50px" }}>

            {/* Cancel */}
            <Box margin="0 10px">
              <OutlineButton
                width="110px"
                height="32px"
                onClick={() => handleCloseModal()}
              >
                Cancel
              </OutlineButton>
            </Box>

            {/* Block / Allow */}
            <Box margin="0 10px">
              <SolidButton
                width="110px"
                height="32px"
                disabled={isInvalid}
                onClick={() => {
                  handleSubmit();
                }}
              >
                {openedAction?.label?.submitButton}
              </SolidButton>
            </Box>

          </Box>
        </>
      }

    </ModalContainer>
  );
};

export default BlockAllowModal;